import { MbeClose } from '@/components/icons';
import { StoresMap } from '@/components/Store';
import { useStores } from '@/hooks';
import type { ILatLng, IMBEStore } from '@/types';
import {
  Button,
  Fade,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useBreakpointValue,
  useControllableState,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StoreInfoBox } from './StoreInfoBox';
import { StoreListItem } from './StoreListItem';
import { StoreSelectorCta } from './StoreSelectorCta';

interface IStoreSelectorProps {
  initialCenter?: ILatLng;
  onSelect: (store: IMBEStore) => void;
  store?: IMBEStore;
}

export const StoreSelector: React.FC<IStoreSelectorProps> = ({
  initialCenter,
  onSelect,
  store,
}): JSX.Element => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isOpenByDefault = useBreakpointValue({ base: false, md: true });
  const { isOpen: isListOpen, onToggle } = useDisclosure({ defaultIsOpen: isOpenByDefault });

  const [selectedStore, setSelectedStore] = useControllableState<IMBEStore | undefined>({
    value: store,
    onChange: (val) => {
      if (val) onSelect(val);
      onClose();
    },
  });

  const { setVisibleStores, stores, visibleStores } = useStores();
  const { t } = useTranslation();

  return (
    <>
      {!selectedStore ? (
        <StoreSelectorCta onOpen={onOpen} />
      ) : (
        <StoreInfoBox store={selectedStore} onOpen={onOpen} />
      )}
      <Modal
        isCentered
        isOpen={isOpen}
        scrollBehavior="inside"
        size={{ base: 'full', md: 'lg' }}
        onClose={(): void => {
          if (isListOpen) onToggle();
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent borderRadius={0} minW={{ base: 'full', md: 'calc(100% - 150px)' }}>
          <ModalBody overflow="hidden" p={0} paddingInlineEnd={0} paddingInlineStart={0}>
            <Flex
              alignItems="center"
              justifyContent={{ base: 'space-between', md: 'flex-end' }}
              p={4}
              position="absolute"
              w="full"
              zIndex={3}
            >
              <Button
                borderRadius="full"
                display={{ base: 'block', md: 'none' }}
                maxWidth="fit-content"
                paddingInline={4}
                textTransform="none"
                variant="mbeSecondary"
                onClick={onToggle}
              >
                {t(isListOpen ? 'STORE.SHOW_MAP' : 'STORE.SHOW_LIST')}
              </Button>
              <ModalCloseButton
                _hover={{ bgColor: 'white' }}
                bgColor="white"
                borderRadius="full"
                h={10}
                left={0}
                position="relative"
                top={0}
                w={10}
              >
                <MbeClose h="3rem" w="3rem" />
              </ModalCloseButton>
            </Flex>
            <Flex>
              <Fade in={isListOpen || isOpenByDefault}>
                <Flex
                  bgColor="white"
                  direction="column"
                  minH="full"
                  minW={{ base: 'full', md: 450 }}
                  // pb={12}
                  position={{ base: 'absolute', md: 'relative' }}
                  zIndex={2}
                >
                  <Heading mb={4} pt={{ base: 20, md: 12 }} px={6} size="md">
                    {t('STORE.LIST_TITLE')}
                  </Heading>
                  <Flex direction="column" flex="1 1 0" gap={6} overflowX="auto" p={6}>
                    {visibleStores.length > 0
                      ? visibleStores.map((store) => (
                          <StoreListItem
                            key={`store-listitem-${store.id}`}
                            store={store}
                            onSelect={setSelectedStore}
                          />
                        ))
                      : t('STORE.EMPTY_LIST')}
                  </Flex>
                </Flex>
              </Fade>
              <Flex flexGrow={1} minHeight={{ base: '100vh', md: 'calc(100vh - 120px)' }}>
                <StoresMap
                  initialCenter={initialCenter}
                  setVisibleStores={setVisibleStores}
                  stores={stores}
                  onSelect={setSelectedStore}
                />
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
